/**
 * Workflow Builder Helpers
 *
 * Convenience functions for constructing common workflow patterns
 * without manually creating nodes and edges.
 *
 * @module gibbs/workflow/builders
 */

import { Workflow } from './Workflow';
import { getAction, isTypeCompatible } from '../registry/index';

/**
 * Build a Workflow DAG from an ActionPlan produced by the text-to-action pipeline.
 *
 * Converts PlannedActions (with step numbers, params, resolvedInputs, inputsFrom)
 * into Workflow nodes and edges, preserving the user's intended parameters and
 * data flow.
 *
 * @param {Object} actionPlan - ActionPlan from planActions()
 * @param {string} actionPlan.intent - 'single_action' or 'workflow'
 * @param {Array<Object>} actionPlan.actions - PlannedAction objects
 * @returns {{ workflow: Workflow, warnings: string[] }}
 */
export function createFromActionPlan(actionPlan) {
    const warnings = [];
    const workflow = new Workflow();
    const actions = actionPlan.actions || [];

    if (actions.length === 0) {
        return { workflow, warnings: ['Empty action plan — no nodes created'] };
    }

    // Step 1: Create nodes, build stepNumber → nodeId map
    const stepToNodeId = new Map();
    const nodeList = [];

    for (const planned of actions) {
        const actionDef = getAction(planned.actionId);
        if (!actionDef) {
            warnings.push(`Unknown actionId: "${planned.actionId}" — node created but may fail validation`);
        }

        // Merge params + resolvedInputs into paramOverrides (resolvedInputs wins on conflict)
        const paramOverrides = {
            ...(planned.params || {}),
            ...(planned.resolvedInputs || {}),
        };

        let node;
        try {
            node = workflow.addNode(planned.actionId, { paramOverrides });
        } catch (e) {
            warnings.push(`Failed to create node for "${planned.actionId}": ${e.message}`);
            continue;
        }

        const step = planned.step || (nodeList.length + 1);
        stepToNodeId.set(step, node.id);
        nodeList.push({ node, planned, step });
    }

    // Step 2: Create edges from inputsFrom references
    for (const { node, planned, step } of nodeList) {
        const inputsFrom = planned.inputsFrom || {};

        for (const [inputName, ref] of Object.entries(inputsFrom)) {
            // Parse "step:N.outputName" format
            const match = typeof ref === 'string' && ref.match(/^step:(\d+)\.(.+)$/);
            if (!match) {
                warnings.push(`Step ${step}: Could not parse inputsFrom reference "${ref}" for input "${inputName}"`);
                continue;
            }

            const sourceStep = parseInt(match[1], 10);
            const outputName = match[2];
            const sourceNodeId = stepToNodeId.get(sourceStep);

            if (!sourceNodeId) {
                warnings.push(`Step ${step}: inputsFrom references step ${sourceStep} which does not exist`);
                continue;
            }

            try {
                workflow.addEdge(sourceNodeId, node.id, outputName, inputName);
            } catch (e) {
                warnings.push(`Step ${step}: Failed to create edge from step ${sourceStep}: ${e.message}`);
            }
        }
    }

    // Step 3: For consecutive nodes with no incoming edges (except the first),
    // try auto-mapping if they have no inputsFrom for required inputs
    for (let i = 1; i < nodeList.length; i++) {
        const { node: targetNode } = nodeList[i];
        const incoming = workflow.getIncomingEdges(targetNode.id);

        if (incoming.length === 0) {
            // No explicit edges — try auto-mapping from previous node
            const { node: sourceNode } = nodeList[i - 1];
            const sourceAction = getAction(sourceNode.actionId);
            const targetAction = getAction(targetNode.actionId);

            if (sourceAction && targetAction) {
                const mapping = findBestMapping(sourceAction, targetAction);
                if (mapping) {
                    for (const { outputName, inputName } of mapping) {
                        try {
                            workflow.addEdge(sourceNode.id, targetNode.id, outputName, inputName);
                        } catch (_) {
                            // Duplicate edge — safe to ignore
                        }
                    }
                }
            }
        }
    }

    return { workflow, warnings };
}

/**
 * Build a linear (sequential) Workflow from an ordered list of action IDs.
 *
 * For each consecutive pair of actions, the builder finds the best
 * type-compatible output→input pairing and creates an edge. If no
 * compatible pairing exists between two consecutive actions, an error
 * is thrown.
 *
 * @param {string[]} actionIds - Ordered list of ActionDef IDs
 * @returns {Workflow} A workflow with nodes connected in sequence
 * @throws {Error} If any actionId is not found in the registry
 * @throws {Error} If no type-compatible output→input pairing exists
 *   between consecutive actions
 *
 * @example
 * const wf = createFromSequence([
 *   'load-protein-from-pdb',
 *   'define-binding-site',
 *   'dock-compound',
 * ]);
 */
export function createFromSequence(actionIds) {
    if (!Array.isArray(actionIds) || actionIds.length === 0) {
        throw new Error('createFromSequence requires a non-empty array of action IDs.');
    }

    const workflow = new Workflow();

    // Validate all action IDs first
    for (const actionId of actionIds) {
        const def = getAction(actionId);
        if (!def) {
            throw new Error(`Unknown actionId: "${actionId}". Not found in registry.`);
        }
    }

    // Create all nodes
    const nodes = actionIds.map((actionId) => workflow.addNode(actionId));

    // Connect consecutive nodes with best type-compatible pairing
    for (let i = 0; i < nodes.length - 1; i++) {
        const sourceNode = nodes[i];
        const targetNode = nodes[i + 1];
        const sourceAction = getAction(sourceNode.actionId);
        const targetAction = getAction(targetNode.actionId);

        const mapping = findBestMapping(sourceAction, targetAction);

        if (!mapping) {
            throw new Error(
                'No type-compatible output→input pairing found between '
                + `"${sourceAction.id}" and "${targetAction.id}". `
                + `Outputs: [${sourceAction.outputs.map((o) => `${o.name}:${o.type}`).join(', ')}]. `
                + `Inputs: [${targetAction.inputs.map((inp) => `${inp.name}:${inp.type}`).join(', ')}].`
            );
        }

        // Create edges for all compatible mappings found
        for (const { outputName, inputName } of mapping) {
            workflow.addEdge(sourceNode.id, targetNode.id, outputName, inputName);
        }
    }

    return workflow;
}

/**
 * Find the best type-compatible output→input mappings between two actions.
 *
 * Strategy:
 * 1. For each required input on the target, find the first compatible output
 *    from the source (prefer exact type match over coercion).
 * 2. If at least one required input can be satisfied, return the mappings.
 * 3. If no required inputs exist, try mapping any compatible output→input pair.
 *
 * @param {import('../registry/schema.js').ActionDef} sourceAction
 * @param {import('../registry/schema.js').ActionDef} targetAction
 * @returns {Array<{outputName: string, inputName: string}>|null}
 *   Array of mappings, or null if no compatible pairing exists
 */
function findBestMapping(sourceAction, targetAction) {
    const mappings = [];
    const usedOutputs = new Set();

    // Collect all target inputs (required first, then optional)
    const targetInputs = [...targetAction.inputs].sort((a, b) => {
        if (a.required && !b.required) return -1;
        if (!a.required && b.required) return 1;
        return 0;
    });

    for (const input of targetInputs) {
        let bestOutput = null;

        for (const output of sourceAction.outputs) {
            if (usedOutputs.has(output.name)) continue;

            if (output.type === input.type) {
                // Exact match — prefer this
                bestOutput = output;
                break;
            } else if (!bestOutput && isTypeCompatible(output.type, input.type)) {
                // Compatible via coercion — use if no exact match found
                bestOutput = output;
            }
        }

        if (bestOutput) {
            mappings.push({
                outputName: bestOutput.name,
                inputName: input.name,
            });
            usedOutputs.add(bestOutput.name);
        }
    }

    return mappings.length > 0 ? mappings : null;
}
